import type { CompanyData } from '@/types';
import { DataConnector, StandardizedConnection, DataConnectionResponse } from '@/types/connections';
import { API_BASE_URL, headers, getAuthHeaders, generateAlphanumericId } from './apiConfig';
import { saveFormDraft } from '@/utils/organization/saveOrganization';
import { getConnectorFriendlyName } from './connectorNames';
import { extractConnectionErrorMessage, isConnectionUnavailableBody } from './connectionSettingsUtils';
import {
  ANONYMOUS_OWNER,
  getOnboardingSessionOwner,
  removeOwnedConnectionIds,
} from '@/utils/onboarding/sessionState';

// fetchDataConnectors, syncFivetranConnector and retryConnector are reachable
// from the public /step/:step onboarding flow, which has no session at all, so
// they stay on the anon-key `headers`. Their edge functions do call requireAuth,
// which means they 401 today — a spec-level gap (weaken the gate vs. require
// auth for onboarding) that no client-side header change can resolve. Tracked
// as AI-INC7-6; do not "fix" it here by forcing a session token.

export const fetchDataConnectors = async (): Promise<DataConnector[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-data-connectors`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API Error (${response.status}):`, errorText);
      throw new Error(`Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    if (!data || !data.metadata) {
      return [];
    }

    const allConnectors = data.metadata || [];

    const allowedConnectors = ['Amazon Selling Partner', 'Shopify', 'Google Sheets', 'NetSuite SuiteAnalytics', 'Walmart Marketplace', 'QuickBooks', 'Sage Intacct', 'Xero', 'Veeqo'];

    const filteredConnectors = allConnectors.filter((connector: DataConnector) =>
      allowedConnectors.includes(connector.name)
    );

    return filteredConnectors;
  } catch (error) {
    console.error('Error fetching data connectors:', error);
    throw error;
  }
};

export const createDataConnection = async (connector: DataConnector, redirectUri?: string, options?: { shop?: string; merchantId?: string; isVendor?: boolean }): Promise<DataConnectionResponse> => {
  try {
    const connectionId = generateAlphanumericId();

    const connectorData = {
      name: connector.name,
      logoUrl: connector.logoUrl,
      id: connector.id,
      connectionId
    };
    sessionStorage.setItem('selectedConnectorData', JSON.stringify(connectorData));

    // Use v2 function for testing 
    const endpoint = 'create-data-connection-v2'
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        connector,
        connectionId,
        redirectUri,
        ...(options?.shop && { shop: options.shop }),
        ...(options?.merchantId && { merchantId: options.merchantId }),
        // Not a truthiness check: isVendor === false (Seller) is a meaningful value
        // that `&&` would silently drop.
        ...(options?.isVendor !== undefined && { isVendor: options.isVendor })
      })
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const data = await response.json();

    const connectorId = data?.data?.connectorId
    const connectionMap = JSON.parse(sessionStorage.getItem('connectionMap')) || {};
    connectionMap[connectorId] = connectionId;
    sessionStorage.setItem('connectionMap', JSON.stringify(connectionMap));

    if (options?.isVendor !== undefined) {
      const accountType = options.isVendor ? 'vendor' : 'seller';
      const accountTypes = JSON.parse(sessionStorage.getItem('amazonAccountTypes') || '{}');
      accountTypes[connectionId] = accountType;
      if (connectorId) accountTypes[connectorId] = accountType;
      sessionStorage.setItem('amazonAccountTypes', JSON.stringify(accountTypes));
    }

    return data;
  } catch (error) {
    console.error('Error creating data connection:', error);
    throw error;
  }
};

/**
 * Placeholder entry for a connection whose stored connector ID belongs to a
 * different Fivetran account. Marked `error` so the list renders it as needing
 * attention, with the reason in `description` instead of a raw HTTP status.
 */
const buildUnavailableConnection = (connectorId: string, message: string): StandardizedConnection => ({
  id: connectorId,
  name: 'Unavailable in this environment',
  status: 'error',
  logoUrl: '',
  service: 'unknown',
  connectorId,
  description: message,
  connectionId: connectorId,
  type: 'unknown',
  created_at: new Date().toISOString(),
  metadata: { unavailableInEnvironment: true },
});

const readAmazonAccountType = (item: { amazon_account_type?: unknown; config?: { is_vendor?: unknown } }, id?: string): 'seller' | 'vendor' | undefined => {
  if (item.amazon_account_type === 'seller' || item.amazon_account_type === 'vendor') {
    return item.amazon_account_type;
  }
  if (item.config?.is_vendor === true) return 'vendor';
  if (item.config?.is_vendor === false) return 'seller';
  try {
    const stored = JSON.parse(sessionStorage.getItem('amazonAccountTypes') || '{}') as Record<string, unknown>;
    const fromSession = id ? stored[id] : undefined;
    if (fromSession === 'seller' || fromSession === 'vendor') return fromSession;
  } catch {
    return undefined;
  }
  return undefined;
};

const isFileImportId = (connectorId: string): boolean => {
  // File imports are UUIDs, while Fivetran connector IDs have different patterns
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidPattern.test(connectorId);
};

export const fetchLinkedConnectors = async (connectorId?: string | string[]): Promise<StandardizedConnection[]> => {
  try {
    if (!connectorId) {
      return [];
    }

    const connectorIds = Array.isArray(connectorId) ? connectorId : [connectorId];

    // Dual-surface: called both from authenticated views (SyncInProgressBanner)
    // and from the public onboarding flow (DataConnectionForm). getAuthHeaders
    // sends the session token when there is one and degrades to the anon key
    // otherwise, so the anonymous path is unchanged.
    const authHeaders = await getAuthHeaders();

    const allConnectors: StandardizedConnection[] = [];

    for (const id of connectorIds) {
      const isCsvImport = id.startsWith('csv-import-');
      const isFileImport = isFileImportId(id);

      // Skip file import IDs - they should be handled by the file import loading logic
      if (isFileImport) {
        continue;
      }

      const url = `${API_BASE_URL}/get-data-linked-connectors?connectorId=${encodeURIComponent(id)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: authHeaders
      });

      if (!response.ok) {
        const rawBody = await response.text();

        // A connection migrated from another Fivetran account cannot resolve here.
        // Surface it as an unavailable entry rather than dropping it from the list,
        // which would make the connection appear to have vanished (UC-168).
        if (isConnectionUnavailableBody(rawBody)) {
          allConnectors.push(buildUnavailableConnection(id, extractConnectionErrorMessage(rawBody)));
          continue;
        }

        console.error(`Error fetching connectors for ID ${id}: ${response.status}`);
        continue;
      }

      const data = await response.json();

      let connectionsData;
      if (data.connections) {
        connectionsData = data.connections;
      } else {
        connectionsData = data;
      }

      const connectionsArray = Array.isArray(connectionsData)
        ? connectionsData
        : (connectionsData && typeof connectionsData === 'object')
          ? [connectionsData]
          : [];
      const linkedConnectors: StandardizedConnection[] = connectionsArray.map((item: any) => {
        let status: "connected" | "pending" | "error" = "pending";
        const setupState =
          item.status && typeof item.status === 'object' ? item.status.setup_state : undefined;

        if (
          item.needs_reconnect ||
          setupState === 'needs_reauth' ||
          setupState === 'broken' ||
          item.metadata?.has_access_token === false
        ) {
          status = "error";
        } else if (
          typeof item.status === 'string' &&
          (item.status === 'expiring' || item.status === 'error' || item.status === 'disconnected')
        ) {
          status = "error";
        } else if (isCsvImport || setupState === "connected" || item.status === 'connected') {
          status = "connected";
        } else if (item.failed_at) {
          status = "error";
        }

        const displayName = isCsvImport
          ? (item.file_name || "CSV Import")
          : (item.display_name || item.name || getConnectorFriendlyName(item.service || item.connector_type || 'Unknown'));

        let iconUrl = item.logo_url || '';
        if (!iconUrl && item.service) {
          iconUrl = `https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/${item.service?.toLowerCase() || 'default'}.svg`;
        } else if (!iconUrl) {
          iconUrl = 'https://via.placeholder.com/40?text=' + displayName.charAt(0);
        }

        // Generate description based on service type
        const description = isCsvImport
          ? (item.csvImports && Array.isArray(item.csvImports) && item.csvImports.length > 0
            ? (item.csvImports.length === 1
              ? `CSV file: ${item.csvImports[0].fileName}`
              : `${item.csvImports.length} CSV files: ${item.csvImports.map((imp: any) => imp.fileName).join(', ')}`)
            : `CSV file: ${item.file_name || 'Unnamed'}`)
          : `${displayName} integration`;

        return {
          id: item?.id,
          name: displayName,
          logoUrl: iconUrl,
          status: status,
          connectionId: item?.connectionId,
          connectorId: item?.id,
          description: description,
          created_at: item?.created_at || new Date().toISOString(),
          service: item.service || (isCsvImport ? 'csv' : 'unknown'),
          csvImports: isCsvImport ? item?.csvImports : undefined,
          type: item.type || item.service || 'unknown',
          metadata: {
            ...item.metadata,
            // Store entire original item for access to all Fivetran data
            originalItem: item,
            status: item.status
          },
          file_name: item.file_name,
          import_type: item.import_type,
          ecommerce_subtype: item.ecommerce_subtype,
          amazon_account_type: readAmazonAccountType(item, item?.id),
          // Pass through Fivetran reconnection properties
          needs_reconnect: item.needs_reconnect,
          reconnect_url: item.reconnect_url,
          setup_state: item?.status?.setup_state
        };
      });

      linkedConnectors.forEach(connector => {
        if (!allConnectors.some(existingConnector => existingConnector.id === connector.id)) {
          allConnectors.push(connector);
        }
      });
    }

    return allConnectors;
  } catch (error) {
    console.error('Error fetching linked connectors:', error);
    return [];
  }
};

export const syncFivetranConnector = async (connectorId: string): Promise<{ success: boolean; error?: string }> => {
  try {

    const response = await fetch(`${API_BASE_URL}/sync-fivetran-connector`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ connectorId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Sync API Error (${response.status}):`, errorText);
      throw new Error(`Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Sync failed');
    }

    return { success: true };
  } catch (error) {
    console.error('Error triggering Fivetran sync:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

const refersToRemovedConnection = (
  value: unknown,
  connectorId: string,
  connectionId: string,
): boolean => {
  if (typeof value !== 'string' || value.length === 0) return false;
  return value === connectorId || value === connectionId;
};

const connectionIsRemoved = (
  connector: unknown,
  connectorId: string,
  connectionId: string,
): boolean => {
  if (!connector || typeof connector !== 'object') return false;
  const record = connector as Record<string, unknown>;
  return (
    refersToRemovedConnection(record.id, connectorId, connectionId) ||
    refersToRemovedConnection(record.connectionId, connectorId, connectionId) ||
    refersToRemovedConnection(record.connectorId, connectorId, connectionId)
  );
};

export const removeConnector = async (connectorId: string, connectionId: string, organisationId?: string): Promise<void> => {
  try {
    const companyData = sessionStorage.getItem('figabl_company_data');
    let parsedOrganisationId: string | undefined;
    if (companyData) {
      try {
        const parsedData = JSON.parse(companyData) as {
          organization_id?: string;
          organisation_id?: string;
          connections?: unknown;
        };
        parsedOrganisationId = parsedData.organization_id || parsedData.organisation_id;

        if (Array.isArray(parsedData.connections)) {
          parsedData.connections = parsedData.connections.filter(
            (connector) => !connectionIsRemoved(connector, connectorId, connectionId),
          );
          sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedData));
          await saveFormDraft(parsedData as CompanyData, { removedConnectionIds: [connectorId, connectionId] });
        }
      } catch (e) {
        console.error('Error updating company data in sessionStorage:', e);
      }
    }

    const owner = getOnboardingSessionOwner();
    const orgIdForSession =
      organisationId ||
      parsedOrganisationId ||
      (owner && owner !== ANONYMOUS_OWNER ? owner : null);
    removeOwnedConnectionIds(orgIdForSession, [connectorId, connectionId]);

    try {
      const rawMap = sessionStorage.getItem('connectionMap');
      if (rawMap) {
        const connectionMap = JSON.parse(rawMap) as Record<string, unknown>;
        if (connectionMap && typeof connectionMap === 'object') {
          delete connectionMap[connectorId];
          delete connectionMap[connectionId];
          for (const [key, value] of Object.entries(connectionMap)) {
            if (refersToRemovedConnection(value, connectorId, connectionId)) {
              delete connectionMap[key];
            }
          }
          sessionStorage.setItem('connectionMap', JSON.stringify(connectionMap));
        }
      }
    } catch (e) {
      console.error('Error updating connectionMap in sessionStorage:', e);
    }

    const sessionKeys = [
      `connector_details_${connectorId}`,
      `connector_details_${connectionId}`,
      `connection_${connectorId}`,
      `connection_${connectionId}`,
      `csv_db_${connectorId}`,
      `csv_db_${connectionId}`,
    ];
    sessionKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error removing connector:', error);
    throw error;
  }
};

export const retryConnector = async (connectorId: string): Promise<DataConnectionResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/retry-data-connector`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ connectorId })
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error retrying connector:', error);
    throw error;
  }
};
